import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Link2, CreditCard, Check, X, Loader2 } from 'lucide-react';
import api from '../api/client';

const planDetails = {
  starter: {
    name: 'Starter',
    price: '$19',
    features: ['250 backlinks', '48h check interval', '3 projects', 'Screenshots', 'Slack alerts'],
  },
  pro: {
    name: 'Pro',
    price: '$49',
    features: ['1,000 backlinks', '24h check interval', '10 projects', 'API access', 'Priority support'],
  },
  agency: {
    name: 'Agency',
    price: '$99',
    features: ['5,000 backlinks', '12h check interval', 'Unlimited projects', 'White-label reports'],
  },
};

function DevCheckout() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session');
  const planId = searchParams.get('plan');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const plan = planDetails[planId] || planDetails.starter;

  useEffect(() => {
    if (!sessionId || !planId) {
      setError('Invalid checkout session');
    }
  }, [sessionId, planId]);

  const handleComplete = async () => {
    setLoading(true);
    setError('');

    try {
      await api.post('/api/billing/dev-complete-checkout', {
        session_id: sessionId,
      });

      // Redirect to settings with success message
      navigate('/settings?success=true');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to complete checkout');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/settings?canceled=true');
  };

  if (error && !sessionId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="h-8 w-8 text-destructive" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Invalid Session</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <button
            onClick={() => navigate('/settings')}
            className="px-6 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors"
          >
            Return to Settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <Link2 className="h-10 w-10 text-primary" />
          <span className="text-2xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
            BacklinkGuard
          </span>
        </div>

        {/* Dev mode badge */}
        <div className="mb-4 p-2 bg-warning/10 border border-warning/30 rounded-lg text-center">
          <span className="text-warning text-sm">Development Mode - Simulated Stripe Checkout</span>
        </div>

        {/* Checkout card */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary to-primary/80 p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
                <CreditCard className="h-6 w-6 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-primary-foreground">Upgrade to {plan.name}</h1>
                <p className="text-primary-foreground/80">Complete your subscription</p>
              </div>
            </div>
          </div>

          {/* Plan details */}
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-muted-foreground text-sm">Monthly subscription</p>
                <p className="text-3xl font-bold text-foreground">{plan.price}<span className="text-lg text-muted-foreground">/mo</span></p>
              </div>
              <div className="text-right">
                <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-sm rounded-full">
                  {plan.name} Plan
                </span>
              </div>
            </div>

            <div className="border-t border-border pt-4 mb-6">
              <p className="text-sm font-medium text-foreground mb-3">What's included:</p>
              <ul className="space-y-2">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Check className="h-4 w-4 text-success shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Simulated payment form */}
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Card number
                </label>
                <input
                  type="text"
                  value="4242 4242 4242 4242"
                  disabled
                  className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-muted-foreground"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Expiry
                  </label>
                  <input
                    type="text"
                    value="12/28"
                    disabled
                    className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-muted-foreground"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    CVC
                  </label>
                  <input
                    type="text"
                    value="123"
                    disabled
                    className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-muted-foreground"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                This is a simulated checkout. No real payment will be processed.
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
                <p className="text-destructive text-sm">{error}</p>
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleCancel}
                disabled={loading}
                className="flex-1 py-2.5 px-4 bg-muted hover:bg-muted/80 text-foreground font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleComplete}
                disabled={loading}
                className="flex-1 py-2.5 px-4 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>Complete Payment</>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-4 text-center text-xs text-muted-foreground">
          Powered by Stripe (Simulated) | Secure checkout
        </p>
      </div>
    </div>
  );
}

export default DevCheckout;
